import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  useConnection,
  useCreateFileSources,
  useProbeBucket,
  useSourceDomains,
  useUpdateFileSources,
} from 'api/connectors';
import { useWarehouses } from 'api/warehouse';
import { usePipelines } from 'api/pipelines';
import { extractApiError } from 'api/errors';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

/*
 * Self-service file/CSV sources (EVOLV-126): a 3-step wizard — bucket (one
 * connection per bucket) → sources (folder → table entries, the tap's
 * tables[] spec) → schedules (one pipeline per schedule group, not per
 * folder: each run costs a container).
 *
 * The wizard is UX, never the control: the backend enforces every contract
 * it leans on — tables[].path pinned to the bucket, hardcoded string-schema
 * flags, streams disjointness across pipelines, dbt_source_key domain list.
 */

const STEPS = () => ([t('data.bucket'), t('data.sources'), t('data.schedules')]);

const CRON_PRESETS = [
  { get label() { return t('data.everyHour'); }, value: '0 * * * *' },
  { get label() { return t('data.every6Hours'); }, value: '0 */6 * * *' },
  { get label() { return t('data.dailyAt2Am'); }, value: '0 2 * * *' },
  { get label() { return t('data.weeklyMon3Am'); }, value: '0 3 * * 1' },
];

const FORMATS = ['csv', 'excel', 'json'];

const keyNames = (value) => [...new Set(value.split(',').map((key) => key.trim()).filter(Boolean))];
const observedColumns = (source) => Array.isArray(source.preview?.column_keys) ? source.preview.column_keys : [];

const PROBE_ERROR_HINTS = {
  get auth() { return t('data.theBucketRejectedTheKeyCheckTheAccessKeyIdAnd'); },
  get not_found() { return t('data.bucketNotFoundCheckTheBucketNameAndTheEndpoint'); },
  get unreachable() { return t('data.endpointUnreachableCheckTheEndpointUrlNetworkTroubleIs'); },
  get blocked() { return t('data.thisEndpointIsNotPubliclyReachablePrivateLoopbackOrInternal'); },
  get config() { return t('data.theProviderRefusedTheRequestThisPointsAtTheEndpointBucket'); },
};

// Suggest a table name from a folder prefix: "Order-Products/2026/" → "order_products".
function suggestTableName(prefix) {
  // LAST segment, not the first: a nested drop (exports/2026/orders/) is a
  // table of orders, not of exports.
  const parts = (prefix || '').split('/').filter(Boolean);
  const head = parts[parts.length - 1] || '';
  const name = head.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
  return /^[a-z]/.test(name) ? name : name ? `t_${name}` : '';
}

const DEFAULT_CRON = '0 2 * * *';

let sourceUid = 0;

function emptySource() {
  sourceUid += 1;
  return {
    uid: sourceUid,
    search_prefix: '',
    // Folders one level under search_prefix, from the probe. null = not
    // resolved yet.
    childFolders: null,
    resolving: false,
    table_name: '',
    nameTouched: false, // user edited the table name — stop auto-suggesting
    key_properties: '',
    keyInput: '',
    format: 'csv',
    delimiter: ',',
    incremental: true,
    partition_column: '',
    group: 1,
    preview: null, // last probe result for this folder
  };
}

export default function FileSourcesWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { connectionId } = useParams(); // present = EDIT mode
  const editing = Boolean(connectionId);
  const probeBucket = useProbeBucket();
  const createFileSources = useCreateFileSources();
  const updateFileSources = useUpdateFileSources();
  const { data: domains } = useSourceDomains();
  const { data: warehouses } = useWarehouses();
  const { data: editConnection } = useConnection(editing ? connectionId : null);
  const { data: allPipelines } = usePipelines();

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [prefilled, setPrefilled] = useState(false);
  const [multiWarehouse, setMultiWarehouse] = useState(false);
  // group number → existing pipeline public_id (edit mode): the PATCH matches
  // by public_id; groups without one are created, absent ones removed.
  const [groupIds, setGroupIds] = useState({});

  // NOTHING is persisted until the final step: every field below is local
  // state, probes are stateless, and the single save at the end is atomic
  // server-side (connection + all pipelines, or nothing).

  // Step 1 state — the bucket (one connection per bucket).
  const [name, setName] = useState('');
  const [bucket, setBucket] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [region, setRegion] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [domain, setDomain] = useState('');
  const [bucketProbe, setBucketProbe] = useState(null);
  // Top-level folders of the bucket, from the step-1 probe. Null = unknown,
  // and the prefix field then accepts free text instead of pretending the
  // bucket is empty.
  const [folders, setFolders] = useState(null);

  // Probe verdicts are advisory and always describe the current inputs.
  // Staleness guard: probes resolve asynchronously while the inputs stay
  // editable — a probe of config B must NOT land its green verdict after the
  // user typed config C. Every edit of a probed field voids in-flight probes.
  const probeTokenRef = useRef(0);
  const setProbedField = (setter) => (e) => {
    setter(e.target.value);
    probeTokenRef.current += 1;
    setBucketProbe(null);
    setFolders(null);
    foldersTriedRef.current = false;
    // Folder previews were observed against the OLD bucket/endpoint: keeping
    // them would judge step 2 by another bucket's columns.
    setSources((prev) => prev.map((s2) => ({ ...s2, preview: null, childFolders: null, resolving: false })));
  };

  // Step 2 state — sources (tables[] entries + partitioning choices).
  const [sources, setSources] = useState([emptySource()]);

  // Step 3 state — schedule per group + destination warehouse.
  const [warehouseId, setWarehouseId] = useState('');
  const [groupCrons, setGroupCrons] = useState({ 1: '0 2 * * *' });

  const activeWarehouses = (warehouses || []).filter((w) => w.is_active);
  const groups = useMemo(
    () => [...new Set(sources.map((s) => s.group))].sort((a, b) => a - b),
    [sources],
  );

  // ── Edit mode: prefill from the saved connection + its live pipelines ─────
  useEffect(() => {
    if (!editing || prefilled) return;
    const fs = editConnection?.file_sources;
    if (!fs || !allPipelines) return;
    const mine = allPipelines
      .filter(
        (p) =>
          p.source_connection?.public_id === connectionId &&
          (p.is_active || p.paused_by_connection || p.paused_by_billing),
      )
      .sort((a, b) => a.created_at.localeCompare(b.created_at));

    setName(editConnection.name);
    setDomain(editConnection.dbt_source_key || '');
    setBucket(fs.s3_bucket);
    setEndpoint(fs.s3_endpoint || '');
    setRegion(fs.region || '');
    // Credentials stay blank: blank secret = "keep the current one".

    const ids = {};
    const crons = {};
    const groupOfTable = {};
    const partitionOfTable = {};
    mine.forEach((p, i) => {
      const group = i + 1;
      ids[group] = p.public_id;
      crons[group] = p.schedule_cron || '';
      (p.options?.streams || []).forEach((t) => {
        groupOfTable[t] = group;
      });
      Object.entries(p.options?.publish?.partition_columns || {}).forEach(
        ([t, col]) => {
          partitionOfTable[t] = col; // string = incremental, null = snapshot
        },
      );
    });
    setGroupIds(ids);
    setGroupCrons(Object.keys(crons).length ? crons : { 1: DEFAULT_CRON });
    if (mine[0]?.warehouse?.public_id) setWarehouseId(mine[0].warehouse.public_id);
    // The wizard has ONE warehouse select — if the live groups currently
    // target different warehouses, saving would silently retarget them all.
    setMultiWarehouse(
      new Set(mine.map((p) => p.warehouse?.public_id ?? 'none')).size > 1,
    );
    setSources(
      fs.tables.map((t) => {
        const partition = partitionOfTable[t.table_name];
        return {
          ...emptySource(),
          search_prefix: t.search_prefix,
          table_name: t.table_name,
          nameTouched: true,
          key_properties: (t.key_properties || []).join(', '),
          format: t.format || 'csv',
          delimiter: t.delimiter || '',
          incremental: partition !== null,
          partition_column: typeof partition === 'string' ? partition : '',
          group: groupOfTable[t.table_name] || 1,
        };
      }),
    );
    setPrefilled(true);
  }, [editing, prefilled, editConnection, allPipelines, connectionId]);

  const stepOneConfig = () => {
    // In edit mode a cleared endpoint/region KEEPS the stored value (the
    // server's blank-keeps-stored merge cannot remove keys), so the probe
    // must test the EFFECTIVE config, not just what is typed.
    const stored = editing ? editConnection?.file_sources : null;
    const effEndpoint = endpoint.trim() || (stored?.s3_endpoint ?? '');
    const effRegion = region.trim() || (stored?.region ?? '');
    return {
      s3_bucket: bucket.trim(),
      ...(effEndpoint ? { s3_endpoint: effEndpoint } : {}),
      ...(effRegion ? { region: effRegion } : {}),
      aws_access_key_id: accessKey.trim(),
      // Pasted secrets often carry edge whitespace — trim, or the probe
      // fails auth with a correct-looking key.
      ...(secretKey.trim() ? { aws_secret_access_key: secretKey.trim() } : {}),
    };
  };

  // Blank credentials while editing mean "keep the stored ones" — those
  // probes run on the SAVED config server-side.
  const usingSavedCreds = editing && !secretKey.trim() && !accessKey.trim();

  // Non-secret fields the tenant may be CHANGING while keeping stored creds:
  // the server layers these over the saved config for the probe.
  const probeOverrides = () => ({
    ...(bucket.trim() ? { s3_bucket: bucket.trim() } : {}),
    ...(endpoint.trim() ? { s3_endpoint: endpoint.trim() } : {}),
    ...(region.trim() ? { region: region.trim() } : {}),
  });

  // ── Step 1: stateless probe — nothing is saved here ───────────────────────
  const handleProbe = async () => {
    setError(null);
    setBucketProbe(null);
    if (!usingSavedCreds && (!bucket.trim() || !accessKey.trim() || !secretKey.trim())) {
      setError(t('data.bucketAccessKeyAndSecretAreRequiredToProbe'));
      return;
    }
    const token = probeTokenRef.current;
    try {
      const probe = await probeBucket.mutateAsync(
        usingSavedCreds
          ? { connectionId, overrides: probeOverrides(), folders: true }
          : { config: stepOneConfig(), folders: true },
      );
      if (token !== probeTokenRef.current) return; // stale: fields changed mid-flight
      setBucketProbe(probe);
      // The folders step 2 offers; null = unknown, see `folders` above.
      setFolders(Array.isArray(probe.folders) ? probe.folders : null);
    } catch (err) {
      if (token !== probeTokenRef.current) return;
      setError(extractApiError(err, t('data.probeFailed')));
    }
  };

  // ── Step 2: per-source helpers ─────────────────────────────────────────────
  // Patch by identity, not index: a folder resolve lands after an await, and
  // the row may have been deleted or reordered meanwhile.
  const patchByUid = (uid, patch) => {
    setSources((prev) => prev.map((s) => (s.uid === uid ? { ...s, ...patch } : s)));
  };

  const patchSource = (index, patch) => {
    const parseChanged = patch.search_prefix !== undefined || patch.format !== undefined || patch.delimiter !== undefined;
    if (parseChanged) probeTokenRef.current += 1;
    setSources((prev) =>
      prev.map((s, i) => {
        if (i !== index) return parseChanged && s.resolving ? { ...s, resolving: false } : s;
        const next = { ...s, ...patch };
        // Auto-name follows the prefix on EVERY keystroke until the user
        // edits the name themselves.
        if (patch.table_name !== undefined) {
          next.nameTouched = patch.table_name !== '';
        } else if (patch.search_prefix !== undefined && !s.nameTouched) {
          next.table_name = suggestTableName(patch.search_prefix);
        }
        // Any input the header parse depends on voids the observed columns:
        // the old ones answered a different folder OR a different parse.
        if (patch.search_prefix !== undefined && patch.childFolders === undefined) {
          next.childFolders = null; // stale: it described the previous folder
        }
        if (parseChanged && patch.preview === undefined) {
          next.preview = null;
          // Also void previews still in flight: they answered the parse this
          // edit just replaced.
          next.resolving = false;
        }
        return next;
      }),
    );
  };

  // Single source of truth for a group's schedule: the payload must send
  // EXACTLY what the select displays, including for a group created on step 3
  // that has no groupCrons entry yet. '' is a real choice ("Manual only").
  const cronFor = (group) => groupCrons[group] ?? DEFAULT_CRON;

  const previewSource = async (index) => {
    const source = sources[index];
    const token = probeTokenRef.current;
    try {
      const prefix = source.search_prefix.trim();
      const result = await probeBucket.mutateAsync(
        usingSavedCreds
          ? { connectionId, prefix, overrides: probeOverrides(), columns: { format: source.format, delimiter: source.delimiter } }
          : { config: stepOneConfig(), prefix, columns: { format: source.format, delimiter: source.delimiter } },
      );
      if (token !== probeTokenRef.current) return; // step-1 config changed mid-flight
      // By identity, and only while the row still shows the prefix this
      // preview answered for.
      setSources((prev) =>
        prev.map((s2) =>
          s2.uid === source.uid && s2.search_prefix.trim() === prefix
            ? { ...s2, preview: result }
            : s2,
        ),
      );
    } catch (err) {
      if (token !== probeTokenRef.current) return;
      setError(extractApiError(err, t('data.previewFailed')));
    }
  };

  // Editing opens straight on step 2, so its folder list cannot come from the
  // step-1 probe: load it when the picker is first opened, once — a bucket
  // that will not answer must not be re-probed on every click.
  const foldersTriedRef = useRef(false);
  const loadFolders = async () => {
    if (foldersTriedRef.current) return;
    foldersTriedRef.current = true;
    const token = probeTokenRef.current;
    try {
      const probe = await probeBucket.mutateAsync(
        usingSavedCreds
          ? { connectionId, overrides: probeOverrides(), folders: true }
          : { config: stepOneConfig(), folders: true },
      );
      if (token !== probeTokenRef.current) return;
      setFolders(Array.isArray(probe.folders) ? probe.folders : null);
    } catch {
      if (token !== probeTokenRef.current) return;
      setFolders(null); // unreachable: free text, never a false "no such folder"
    }
  };

  // What is INSIDE the chosen folder — the only way to tell a leaf from a
  // folder of folders, since file counts are recursive. One probe answers
  // both: its sub-folders, and whether it holds files at all.
  const resolveFolder = async (uid, prefix) => {
    const value = (prefix ?? '').trim();
    if (!value) {
      patchByUid(uid, { childFolders: null, preview: null, resolving: false });
      return;
    }
    const token = probeTokenRef.current;
    const source = sources.find((item) => item.uid === uid);
    const columns = { format: source?.format || 'csv', delimiter: source?.delimiter || '' };
    patchByUid(uid, { resolving: true });
    try {
      const probe = await probeBucket.mutateAsync(
        usingSavedCreds
          ? { connectionId, prefix: value, overrides: probeOverrides(), folders: true, columns }
          : { config: stepOneConfig(), prefix: value, folders: true, columns },
      );
      if (token !== probeTokenRef.current) return;
      patchByUid(uid, {
        childFolders: Array.isArray(probe.folders) ? probe.folders : null,
        preview: probe,
        resolving: false,
      });
    } catch {
      if (token !== probeTokenRef.current) return;
      patchByUid(uid, { childFolders: null, resolving: false });
    }
  };

  // A folder the bucket does not have. The listed folders are top level only,
  // so a nested path is legitimately absent from them — a preview that FOUND
  // files outranks the list. An unknown list (null) is never an error.
  const folderUnknown = (source) => {
    const value = (source.search_prefix ?? '').trim();
    if (!folders || !value) return false;
    if (folders.includes(value) || folders.includes(`${value}/`)) return false;
    return !(source.preview?.matched_count > 0);
  };

  // A load takes everything UNDER the folder, sub-folders included (owner's
  // call — date-partitioned drops need it). Allowed, but never silently.
  const folderNote = (source) => {
    const n = source.childFolders?.length ?? 0;
    if (!n) return '';
    return t('data.holdsValueSubFolderSFilesInsideThemLoadIntoThis', { value1: n });
  };

  const sourcesValid =
    sources.length > 0 &&
    sources.every(
      (s) =>
        s.search_prefix.trim() &&
        /^[a-z][a-z0-9_]*$/.test(s.table_name) &&
        (!s.incremental || s.partition_column.trim()),
    ) &&
    new Set(sources.map((s) => s.table_name)).size === sources.length;

  const handleContinueSources = () => {
    setError(null);
    // Purely local: sources travel to the server only in the final save.
    setActiveStep(2);
  };

  // ── Step 3: ONE atomic save — connection + a pipeline per group ───────────
  const handleCreateAll = async () => {
    setError(null);
    if (!name.trim()) {
      setError(t('data.connectionNameIsRequired'));
      return;
    }
    if (!warehouseId) {
      setError(t('data.pickADestinationWarehouse'));
      return;
    }
    if (groups.length > 12) {
      setError(t('data.atMost12ScheduleGroupsPerBucketMergeSomeGroups'));
      return;
    }
    const pipelines = groups.map((group) => {
      const members = sources.filter((s) => s.group === group);
      const partitionColumns = {};
      members.forEach((s) => {
        partitionColumns[s.table_name] = s.incremental ? s.partition_column.trim() : null;
      });
      return {
        // Edit mode: match the existing pipeline of this group.
        ...(editing && groupIds[group] ? { public_id: groupIds[group] } : {}),
        name:
          groups.length === 1
            ? t('data.filesName', { name: name.trim() })
            : t('data.valueFilesGroupValue', { value1: name.trim(), value2: group }),
        warehouse_id: warehouseId,
        schedule_cron: cronFor(group),
        is_active: true,
        options: {
          streams: members.map((s) => s.table_name),
          publish: { partition_columns: partitionColumns },
        },
      };
    });
    const payload = {
      connection: {
        name: name.trim(),
        ...(editing ? {} : { connector_type_slug: 's3-files' }),
        dbt_source_key: domain,
        config: {
          ...stepOneConfig(),
          tables: sources.map((s) => ({
            table_name: s.table_name,
            search_prefix: s.search_prefix.trim(),
            key_properties: keyNames([s.key_properties, s.keyInput].join(',')),
            format: s.format,
            ...(s.delimiter ? { delimiter: s.delimiter } : {}),
          })),
        },
      },
      pipelines,
    };
    try {
      if (editing) {
        await updateFileSources.mutateAsync({ id: connectionId, ...payload });
      } else {
        await createFileSources.mutateAsync(payload);
      }
      navigate('/console/pipelines');
    } catch (err) {
      // Server-side failure of ANY part rolled the WHOLE save back — point at
      // the failing schedule group when the error is pipeline-indexed.
      const indexed = err?.response?.data?.pipelines;
      if (indexed && typeof indexed === 'object' && !Array.isArray(indexed)) {
        const index = Object.keys(indexed)[0];
        const group = groups[Number(index)];
        setError(
          t('data.groupValueValueNothingWasSavedFixAndCreateAgain', { value1: group ?? index, value2: extractApiError(
            { response: { data: indexed[index] } },
            t('data.failedToCreateThePipeline'),
          ) }),
        );
        return;
      }
      setError(extractApiError(err, t('data.failedToCreateTheFileSources')));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/console/connectors')}
          size="small"
          color="inherit"
        >{t('data.back')}</Button>
        <Divider orientation="vertical" flexItem />
        <FolderOpenIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {editing ? t('data.editFileSourceS3Csv') : t('data.newFileSourceS3Csv')}
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4, maxWidth: 560 }}>
        {STEPS().map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 720 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Step 1: Bucket ──────────────────────────────────────────────── */}
      {activeStep === 0 && (
        <Box sx={{ maxWidth: 560 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.oneConnectionPerBucketCredentialsAreEnteredOnceAndReusedBy')}{' '}<b>{t('data.readOnly')}</b>{' '}{t('data.keyForYourBucket')}</Typography>
          <TextField
            label={t('data.connectionName2')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('data.s3Endpoint')}
            value={endpoint}
            onChange={setProbedField(setEndpoint)}
            disabled={probeBucket.isPending}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="https://s3.cloud.ru"
            helperText={
              editing
                ? t('data.leaveBlankToKeepTheCurrentEndpointClearingCannotRemoveIt')
                : t('data.leaveEmptyForAwsMustBeAPublicHttpsEndpointCloud')
            }
          />
          <TextField
            label={t('data.bucket')}
            value={bucket}
            onChange={setProbedField(setBucket)}
            disabled={probeBucket.isPending}
            required
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('data.region')}
            value={region}
            onChange={setProbedField(setRegion)}
            disabled={probeBucket.isPending}
            fullWidth
            sx={{ mb: 2 }}
            helperText={
              endpoint.trim() && !region.trim()
                ? t('data.customEndpointsUsuallyRequireARegionForRequestSigningCloudRu')
                : editing
                  ? t('data.leaveBlankToKeepTheCurrentValue')
                  : t('data.optionalMostS3CompatibleProvidersIgnoreIt')
            }
          />
          <TextField
            label={t('data.accessKeyId')}
            value={accessKey}
            onChange={setProbedField(setAccessKey)}
            disabled={probeBucket.isPending}
            required={!editing}
            fullWidth
            sx={{ mb: 2 }}
            helperText={
              editing
                ? t('data.leaveBlankToKeepTheCurrentKey')
                : t('data.cloudRuUsesTheCompositeFormTenantIdKeyId')
            }
          />
          <TextField
            label={t('data.secretAccessKey')}
            type="password"
            value={secretKey}
            onChange={setProbedField(setSecretKey)}
            disabled={probeBucket.isPending}
            required={!editing}
            fullWidth
            sx={{ mb: 2 }}
            helperText={editing ? t('data.leaveBlankToKeepTheCurrentSecret') : ''}
          />
          <TextField
            select
            label={t('data.reportDomain')}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText={t('data.whichStandardReportFamilyThisDataFeedsEGOrdersUnlocks')}
          >
            <MenuItem value="">{t('data.notMapped')}</MenuItem>
            {(domains || []).map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>

          {bucketProbe && (
            <Alert
              severity={bucketProbe.reachable ? 'success' : 'error'}
              icon={bucketProbe.reachable ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
              sx={{ mb: 2 }}
            >
              {bucketProbe.reachable
                ? t('data.bucketReachableValuevalueObjectSVisible', { value1: bucketProbe.matched_count, value2: bucketProbe.truncated ? '+' : '' })
                : (PROBE_ERROR_HINTS[bucketProbe.error] || t('data.probeFailed')) +
                  (bucketProbe.s3_code ? t('data.s3Error', { message: bucketProbe.s3_code }) : '') +
                  (bucketProbe.error === 'auth' && endpoint.includes('cloud.ru')
                    ? t('data.cloudRuKeysUseTheCompositeFormTenantIdKeyId')
                    : '')}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={
                probeBucket.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <TravelExploreIcon />
                )
              }
              onClick={handleProbe}
              disabled={probeBucket.isPending}
            >{t('data.probeBucket')}</Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!name.trim() || !bucket.trim() || (!usingSavedCreds && (!accessKey.trim() || !secretKey.trim()))}
            >{t('data.nextSources')}</Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {editing
              ? t('data.changesApplyOnlyAtTheLastStepConnectionAndPipelinesTogether')
              : t('data.nothingIsSavedYetTheConnectionAndItsPipelinesAreCreated')}
          </Typography>
        </Box>
      )}

      {/* ── Step 2: Sources ─────────────────────────────────────────────── */}
      {activeStep === 1 && (
        <Box sx={{ maxWidth: 860 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.oneSourceOneFolderInTheBucketOneRawTablePreview')}</Typography>

          {sources.map((source, index) => (
            <Paper
              key={source.uid}
              variant="outlined"
              sx={{ p: 2, mb: 2, borderRadius: 2 }}
            >
              {/* ── Source settings: the folder + its live preview ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">{t('data.sourceSettings')}</Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={t('data.removeThisSource')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setSources((prev) => prev.filter((_, i) => i !== index))}
                      disabled={sources.length === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Autocomplete
                freeSolo
                onOpen={loadFolders}
                // Offer what is INSIDE the chosen folder once we know it, so a
                // nested layout is walked down to a leaf instead of guessed.
                options={
                  source.childFolders?.length ? source.childFolders : (folders ?? [])
                }
                loading={source.resolving}
                value={source.search_prefix}
                inputValue={source.search_prefix}
                onInputChange={(_e, value) =>
                  patchSource(index, { search_prefix: value ?? '' })
                }
                onChange={(_e, value) => {
                  patchSource(index, { search_prefix: value ?? '' });
                  resolveFolder(source.uid, value ?? '');
                }}
                // Typed by hand: resolve when the field is left, not per
                // keystroke — one probe per folder, not one per character.
                onBlur={() => resolveFolder(source.uid, source.search_prefix)}
                // Discovery is advisory; sources can be saved before exports arrive.
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('data.folder')}
                    required
                    fullWidth
                    placeholder="orders/"
                    helperText={
                      folderUnknown(source)
                        ? t('data.folderNotObserved')
                        : folderNote(source) ||
                          (folders
                            ? t('data.pickAFolderFromTheBucketOrTypeANestedPath')
                            : t('data.pathInsideTheBucketEGOrders'))
                    }
                  />
                )}
              />
              {/* Preview. */}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1, minHeight: 32 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TravelExploreIcon />}
                  onClick={() => previewSource(index)}
                  disabled={!source.search_prefix.trim() || probeBucket.isPending}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {t('data.previewFiles')}
                </Button>
                {source.preview && (
                  <Chip
                    size="small"
                    sx={{ flexShrink: 0 }}
                    color={source.preview.matched_count > 0 ? 'success' : 'warning'}
                    label={
                      source.preview.reachable
                        ? t('data.filePreviewCount', { amount: source.preview.matched_count, more: source.preview.truncated ? '+' : '' })
                        : t('data.probeFailed2')
                    }
                  />
                )}
                {source.preview?.sample?.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontFamily: 'monospace', minWidth: 0 }}
                  >
                    {source.preview.sample.slice(0, 3).join(' · ')}
                    {source.preview.matched_count > 3 ? ' · …' : ''}
                  </Typography>
                )}
              </Box>

              {/* ── Table settings: what the folder becomes ── */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2.5, mb: 1.5 }}>{t('data.tableSettings')}</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  label={t('data.tableName')}
                  value={source.table_name}
                  onChange={(e) => patchSource(index, { table_name: e.target.value })}
                  required
                  sx={{ minWidth: 180 }}
                  error={!!source.table_name && !/^[a-z][a-z0-9_]*$/.test(source.table_name)}
                  helperText={t('data.lowercaseLettersDigitsUnderscores')}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={observedColumns(source)}
                  value={keyNames(source.key_properties)}
                  inputValue={source.keyInput}
                  onInputChange={(_e, value) => patchSource(index, { keyInput: value })}
                  onChange={(_e, values) => patchSource(index, { key_properties: keyNames(values.join(',')).join(', '), keyInput: '' })}
                  onBlur={() => patchSource(index, { key_properties: keyNames([source.key_properties, source.keyInput].join(',')).join(', '), keyInput: '' })}
                  sx={{ minWidth: 220, flex: '1 1 260px' }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('data.fileKeyColumnsOptional')} placeholder="order_id"
                      helperText={t('data.fileKeyColumnsHint')} />
                  )}
                />
                <TextField
                  select
                  label={t('data.format')}
                  value={source.format}
                  onChange={(e) => patchSource(index, { format: e.target.value })}
                  sx={{ width: 110 }}
                >
                  {FORMATS.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </TextField>
                {source.format === 'csv' && (
                  <TextField
                    label={t('data.delimiter')}
                    value={source.delimiter}
                    onChange={(e) => patchSource(index, { delimiter: e.target.value })}
                    helperText={t('data.emptyDetect')}
                    placeholder={t('data.auto')}

                    sx={{ width: 100 }}
                  />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                {observedColumns(source).length ? t('data.fileColumnSuggestionsHint') : t('data.fileColumnsManualHint')}
              </Typography>
              {/* Load mode. */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1.5, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={source.incremental}
                      onChange={(e) => patchSource(index, { incremental: e.target.checked })}
                    />
                  }
                  label={source.incremental ? t('data.fileUpdatesByFile') : t('data.snapshotFullReplace')}
                />
                {source.incremental ? (
                  <Autocomplete
                    freeSolo
                    options={observedColumns(source)}
                    value={source.partition_column}
                    inputValue={source.partition_column}
                    onInputChange={(_e, value) => patchSource(index, { partition_column: value || '' })}
                    onChange={(_e, value) => patchSource(index, { partition_column: value || '' })}
                    sx={{ minWidth: 200, flex: '1 1 240px' }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('data.dateColumn')} required size="small"
                        helperText={t('data.filePartitionHint')} />
                    )}
                  />
                ) : (
                  <Typography variant="caption" color="warning.main" sx={{ maxWidth: 380 }}>{t('data.fileSnapshotHint')}</Typography>
                )}
              </Box>
              {source.incremental && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('data.fileUpdatesHint')}</Typography>}
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={() => setSources((prev) => [...prev, { ...emptySource(), group: 1 }])}
            sx={{ mb: 3 }}
          >{t('data.addSource')}</Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(0)}>{t('data.back')}</Button>
            <Button
              variant="contained"
              onClick={handleContinueSources}
              disabled={!sourcesValid}
            >{t('data.continue')}</Button>
          </Box>
        </Box>
      )}

      {/* ── Step 3: Schedules ───────────────────────────────────────────── */}
      {activeStep === 2 && (
        <Box sx={{ maxWidth: 720 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.sourcesSharingAScheduleRunAsOnePipelineEachRunStarts')}</Typography>

          {multiWarehouse && (
            <Alert severity="warning" sx={{ mb: 2 }}>{t('data.theseGroupsCurrentlyLoadIntoDifferentWarehousesSavingWillPointEvery')}</Alert>
          )}
          <TextField
            select
            label={t('data.destinationWarehouse2')}
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            required
            fullWidth
            sx={{ mb: 3 }}
          >
            {activeWarehouses.map((w) => (
              <MenuItem key={w.public_id} value={w.public_id}>
                {w.name}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({w.engine_display})
                </Typography>
              </MenuItem>
            ))}
          </TextField>

          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            {sources.map((source, index) => (
              <Box
                key={source.uid}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}
              >
                <Typography sx={{ minWidth: 200, fontFamily: 'monospace' }}>
                  {source.table_name}
                </Typography>
                <Chip
                  size="small"
                  label={source.incremental ? t('data.dailyByValue', { value1: source.partition_column }) : t('data.snapshot')}
                  color={source.incremental ? 'primary' : 'default'}
                  variant="outlined"
                />
                <Box sx={{ flex: 1 }} />
                <TextField
                  select
                  label={t('data.group')}
                  size="small"
                  value={source.group}
                  onChange={(e) => patchSource(index, { group: Number(e.target.value) })}
                  sx={{ width: 120 }}
                >
                  {[...groups, Math.max(...groups) + 1].map((g) => (
                    <MenuItem key={g} value={g}>{t('data.group')}{' '}{g}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            ))}
          </Paper>

          {groups.map((group) => (
            <Box key={group} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography sx={{ minWidth: 100 }} fontWeight={600}>{t('data.group')}{' '}{group}
              </Typography>
              <TextField
                select
                label={t('data.schedule')}
                size="small"
                value={cronFor(group)}
                onChange={(e) => setGroupCrons((prev) => ({ ...prev, [group]: e.target.value }))}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">{t('data.manualOnly')}</MenuItem>
                {CRON_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveStep(1)}
              disabled={createFileSources.isPending || updateFileSources.isPending}
            >{t('data.back')}</Button>
            <Button
              variant="contained"
              onClick={handleCreateAll}
              disabled={createFileSources.isPending || updateFileSources.isPending || !warehouseId}
              startIcon={
                createFileSources.isPending || updateFileSources.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {editing ? t('data.saveChanges') : t('data.createConnectionPipelines')}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>{t('data.oneAtomicSaveIfAnythingFailsToValidateNothingIsCreated')}</Typography>
        </Box>
      )}
    </Box>
  );
}
